import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminPage from '../components/AdminShell';
import './MarketingPages.css';

const US_STATES = [
  'AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL',
  'IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE',
  'NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VA','VT','WA','WI','WV','WY',
];

const statusColor = (status) => {
  const colors = {
    pending: '#f59e0b', approved: '#3b82f6', discovering: '#06b6d4',
    teams_discovered: '#8b5cf6', scraping: '#8b5cf6', completed: '#22c55e', failed: '#ef4444',
  };
  return colors[status] || '#94a3b8';
};

// Queue chips: Complete green, Running amber, Queued neutral, Failed red.
const STATUS_CHIP = {
  completed: ['Complete', 'adm-chip--green'],
  scraping: ['Running', 'adm-chip--amber'], discovering: ['Running', 'adm-chip--amber'], teams_discovered: ['Running', 'adm-chip--amber'],
  pending: ['Queued', ''], approved: ['Queued', ''],
  failed: ['Failed', 'adm-chip--red'],
};

const formatSport = (s) => s
  .replace(/_/g, ' ')
  .replace(/\b\w/g, c => c.toUpperCase())
  .replace('And', '&')
  .replace('Tenpin Bowling', 'Bowling')
  .replace('Dance & Drill', 'Dance/Drill')
  .replace('Cheer & Spirit', 'Cheer')
  .replace('Field Hockey', 'Fld Hockey')
  .replace('Cross Country', 'X-Country')
  .replace('Swimming & Diving', 'Swim/Dive')
  .replace('Track & Field', 'Track/Field')
  .replace('Water Polo', 'Water Polo');

export default function MarketingDashboardPage() {
  const { API_URL } = useAuth();
  const MARKETING_URL = `${API_URL.replace('/api', '')}/marketing`;
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [stateScraped, setStateScraped] = useState({});
  const [stateTotals, setStateTotals] = useState({});
  const [sportData, setSportData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fmtK = (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v.toString();

  const fmtDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';
    const secs = Math.round((new Date(completedAt) - new Date(startedAt)) / 1000);
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  const fetchAll = useCallback(async () => {
    try {
      const [statsRes, jobsRes, stateRes, sportRes] = await Promise.all([
        axios.get(`${MARKETING_URL}/players/stats`, { headers }),
        axios.get(`${MARKETING_URL}/scrape/jobs`, { headers }),
        axios.get(`${MARKETING_URL}/schools/stats/by-state`, { headers }),
        axios.get(`${MARKETING_URL}/schools/stats/by-sport`, { headers }),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setStateScraped(stateRes.data);
      // Filter sports with players > 0, sort alphabetically
      const filtered = sportRes.data.filter(s => s.players > 0);
      setSportData([...filtered].sort((a, b) => a.sport.localeCompare(b.sport)));
      setLoadError(false);
    } catch (err) {
      console.error('Error loading marketing data:', err);
      setLoadError(true);
    }
    setLoading(false);
  }, [MARKETING_URL]);

  useEffect(() => {
    fetch('/data/high-schools.json')
      .then(r => r.json())
      .then(data => {
        const counts = {};
        data.forEach(s => { if (s.s) counts[s.s] = (counts[s.s] || 0) + 1; });
        setStateTotals(counts);
      })
      .catch(err => console.error('Error loading schools data:', err));
  }, []);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const queueAction = <Link to="/marketing/schools?add=1" className="adm-btn adm-btn--primary">+ Queue school</Link>;
  if (loading) {
    return <AdminPage active="mkt-dash" title="Marketing" actions={queueAction}><p className="adm-loading">Loading…</p></AdminPage>;
  }
  if (loadError && !stats) {
    return <AdminPage active="mkt-dash" title="Marketing" actions={queueAction}><p className="adm-error" role="alert">Could not load marketing data.</p></AdminPage>;
  }

  // State chart — only US states present in our schools list
  const statesWithData = US_STATES.filter(st => stateTotals[st] > 0);

  // Sports chart
  const maxPlayers = Math.max(...sportData.map(s => s.players), 1);
  const topSportName = sportData.reduce((top, s) => s.players > (top?.players || 0) ? s : top, null)?.sport;
  const BAR_H = 160;

  const kpi = (label, value, hero = false) => (
    <div className={hero ? 'adm-kpi adm-kpi--hero' : 'adm__card adm-kpi'}>
      <span className={`adm-kpi__label ${hero ? '' : 'adm-kpi__label--dark'}`}>{label}</span>
      <div className={`adm-kpi__value ${hero ? '' : 'adm-kpi__value--dark'}`}>{value}</div>
    </div>
  );

  return (
    <AdminPage active="mkt-dash" title="Marketing" subtitle="Data mining and outreach pipeline" actions={queueAction}>
      {stats && (
        <div className="adm-kpis">
          {kpi('Total players', stats.total_players.toLocaleString(), true)}
          {kpi('Profiles scraped', stats.profiles_scraped.toLocaleString())}
          {kpi('With social', stats.with_social.toLocaleString())}
          {kpi('With email', stats.with_email.toLocaleString())}
          {kpi('Schools scraped', stats.schools_scraped)}
        </div>
      )}

      {/* Scrape queue — last 10 */}
      <div className="adm__card adm__card--flush mkt-queue">
        <div className="mkt-queue__head">
          <h2 className="mkt-queue__title">Scrape queue</h2>
          <Link to="/marketing/schools" className="srch-link">All schools →</Link>
        </div>
        {jobs.length === 0 ? (
          <p className="adm-empty">No scrape jobs yet.</p>
        ) : (
          <div className="adm-tablewrap">
            <table className="adm-table">
              <thead>
                <tr><th>SCHOOL</th><th>STATUS</th><th>PLAYERS</th><th>COMPLETED</th><th>TIME</th></tr>
              </thead>
              <tbody>
                {jobs.slice(0, 10).map((j) => {
                  const [label, tone] = STATUS_CHIP[j.status] || [j.status, ''];
                  return (
                    <tr key={j.id}>
                      <td>
                        <span className="mkt-school">{j.target_name}</span>
                        {j.target_state && <span className="mkt-sub">{j.target_state}</span>}
                      </td>
                      <td><span className={`adm-chip ${tone}`}>{label}</span></td>
                      <td>{(j.players_found || 0).toLocaleString()}</td>
                      <td className="mkt-muted">{j.completed_at ? new Date(j.completed_at).toLocaleString() : '-'}</td>
                      <td className="mkt-muted">{fmtDuration(j.started_at, j.completed_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Two-column chart layout: 25% state coverage, 75% sport chart */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: '24px', marginBottom: '24px', minWidth: 0 }}>

        {/* State Coverage Chart (25%) */}
        <div className="adm__card" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '10px' }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Coverage by State</div>
          </div>
          <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b', marginBottom: '10px', flexWrap: 'wrap' }}>
            <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#22c55e', borderRadius: '2px', marginRight: '4px' }} />≥50%</span>
            <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#3b82f6', borderRadius: '2px', marginRight: '4px' }} />{'<50%'}</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '4px 24px',
            maxHeight: '240px',
            overflowY: 'auto',
            paddingRight: '20px',
          }}>
            {statesWithData.map(st => {
              const total = stateTotals[st] || 0;
              const scraped = stateScraped[st] || 0;
              const pct = total > 0 ? Math.round((scraped / total) * 100) : 0;
              return (
                <div key={st} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                  <div style={{ width: '24px', fontWeight: 600, color: '#374151', flexShrink: 0 }}>{st}</div>
                  <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '3px', height: '12px', position: 'relative', overflow: 'hidden' }}>
                    <div style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: pct >= 50 ? '#22c55e' : pct > 0 ? '#3b82f6' : '#e5e7eb',
                      borderRadius: '3px',
                    }} />
                  </div>
                  <div style={{ width: '68px', textAlign: 'right', color: '#64748b', flexShrink: 0, fontSize: '11px' }}>
                    {scraped}/{total}
                    {pct > 0 && <span style={{ color: pct >= 50 ? '#22c55e' : '#3b82f6', marginLeft: '3px', fontWeight: 700 }}>{pct}%</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Players by Sport Chart (75%) */}
        <div className="adm__card" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '12px' }}>
            <div className="card-title" style={{ marginBottom: 0 }}>Players by Sport</div>
            <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#64748b' }}>
              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#ef4444', borderRadius: '2px', marginRight: '4px' }} />Top sport</span>
              <span><span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#93c5fd', borderRadius: '2px', marginRight: '4px' }} />Others</span>
            </div>
          </div>
          {sportData.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: '14px' }}>No sport data yet.</p>
          ) : (
            <div>
              {/* Y-axis labels + bars */}
              <div style={{ display: 'flex', gap: '0' }}>
                {/* Y-axis */}
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: `${BAR_H}px`, marginRight: '6px', flexShrink: 0, width: '30px' }}>
                  {[maxPlayers, Math.round(maxPlayers * 0.75), Math.round(maxPlayers * 0.5), Math.round(maxPlayers * 0.25), 0].map(v => (
                    <div key={v} style={{ fontSize: '10px', color: '#94a3b8', textAlign: 'right', lineHeight: 1 }}>
                      {fmtK(v)}
                    </div>
                  ))}
                </div>

                {/* Chart area — paddingLeft gives leftmost label room to extend without clipping */}
                <div style={{ flex: 1, minWidth: 0, paddingLeft: '36px' }}>
                  {/* Bars with embedded gridlines */}
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', height: `${BAR_H}px`, gap: '2px' }}>
                    {[0, 25, 50, 75, 100].map(pct => (
                      <div key={pct} style={{
                        position: 'absolute', left: 0, right: 0,
                        bottom: `${pct}%`, height: '1px',
                        background: pct === 0 ? '#cbd5e1' : '#f1f5f9',
                        pointerEvents: 'none',
                      }} />
                    ))}
                    {sportData.map(s => {
                      const isTop = s.sport === topSportName;
                      const playerH = Math.max(Math.round((s.players / maxPlayers) * BAR_H), 2);
                      return (
                        <div key={s.sport} style={{ flex: '1 1 0', height: '100%', display: 'flex', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
                          <div
                            style={{
                              width: '100%',
                              height: `${playerH}px`,
                              background: isTop ? '#ef4444' : '#93c5fd',
                              borderRadius: '3px 3px 0 0',
                              transition: 'height 0.3s ease',
                            }}
                            title={`${formatSport(s.sport)}: ${s.players.toLocaleString()} players`}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* X-axis baseline */}
                  <div style={{ height: '1px', background: '#cbd5e1' }} />

                  {/* X-axis labels — right edge anchored at bar center, -45° rotation extends upper-left */}
                  <div style={{ position: 'relative', height: '75px' }}>
                    <div style={{ display: 'flex', gap: '2px', position: 'absolute', inset: 0 }}>
                      {sportData.map(s => {
                        const isTop = s.sport === topSportName;
                        return (
                          <div key={s.sport} style={{ flex: '1 1 0', position: 'relative' }}>
                            <span style={{
                              position: 'absolute',
                              top: '5px',
                              right: '50%',
                              transform: 'rotate(-45deg)',
                              transformOrigin: 'top right',
                              whiteSpace: 'nowrap',
                              fontSize: '9px',
                              color: isTop ? '#ef4444' : '#374151',
                              fontWeight: isTop ? 700 : 400,
                              lineHeight: 1,
                            }}>
                              {formatSport(s.sport)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminPage>
  );
}
